import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import type { ValidationSummaryResult } from './validation-summary';

interface ValidationSummaryPanelProps {
  summary: ValidationSummaryResult;
  /** Total rows in the dataset, for context. */
  totalRows: number;
}

const MAX_FIELDS_SHOWN = 8;

export function ValidationSummaryPanel({
  summary,
  totalRows,
}: ValidationSummaryPanelProps) {
  if (summary.totalIssues === 0) {
    return (
      <div className='flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900'>
        <CheckCircle2 className='size-4 shrink-0' />
        <span>All {totalRows} rows look good. No validation issues found.</span>
      </div>
    );
  }

  const listed = summary.issuesByField.slice(0, MAX_FIELDS_SHOWN);
  const remaining = summary.issuesByField.length - listed.length;

  return (
    <div className='space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
      <div className='flex items-center gap-2 font-medium'>
        <AlertTriangle className='size-4 shrink-0' />
        <span>
          {summary.totalIssues} issue
          {summary.totalIssues === 1 ? '' : 's'} across{' '}
          {summary.affectedRowCount} of {totalRows} row
          {totalRows === 1 ? '' : 's'}
        </span>
      </div>
      <p className='text-amber-800'>
        Highlighted (pink) cells below need attention. Fix them in your CSV and
        re-upload for the cleanest import, or continue and correct them later.
      </p>
      <ul className='ml-1 space-y-1'>
        {listed.map((group) => (
          <li key={group.field}>
            <span className='font-medium'>{group.field}</span>: {group.count}{' '}
            issue{group.count === 1 ? '' : 's'} — {group.sampleMessage}
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <p className='text-xs text-amber-800'>
          And {remaining} more field{remaining === 1 ? '' : 's'} with issues.
        </p>
      )}
    </div>
  );
}
